import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Lock, Plus, Power, Trash2, Webhook } from 'lucide-react';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';
import { toast } from '../lib/toast';

function hasAutoPilotFeature(user) {
  const ap = user?.entitlements?.autoPilot;
  if (!ap?.active) return false;
  if (!ap.expiresAt) return true;
  return new Date(ap.expiresAt).getTime() > Date.now();
}

// Per-event labels for the events multi-select.
const EVENT_LABELS = {
  'audit.completed': 'Audit completed',
  'rewrite.completed': 'Rewrite completed',
  'score.dropped': 'Score dropped',
  'bot.detected': 'AI bot detected',
};

export default function UserWebhooksCard() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const locked = !hasAutoPilotFeature(user);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    label: '',
    url: '',
    events: ['audit.completed'],
  });
  const [revealedSecrets, setRevealedSecrets] = useState({}); // id -> secret

  const webhooksQuery = useQuery({
    queryKey: ['user-webhooks'],
    queryFn: () => api.get('/api/me/webhooks').then((r) => r.data),
    enabled: !locked,
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post('/api/me/webhooks', body).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['user-webhooks'] });
      // Stash the full secret so we can render it once with a copy button.
      setRevealedSecrets((prev) => ({
        ...prev,
        [data.webhook._id]: data.webhook.secret,
      }));
      setAdding(false);
      setDraft({ label: '', url: '', events: ['audit.completed'] });
      toast.success('Webhook created — save the signing secret now');
    },
    onError: (err) => toast.error(err, 'Could not create webhook'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) =>
      api.put(`/api/me/webhooks/${id}`, { isActive }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-webhooks'] });
      toast.success('Webhook updated');
    },
    onError: (err) => toast.error(err, 'Could not update webhook'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/me/webhooks/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-webhooks'] });
      toast.success('Webhook deleted');
    },
    onError: (err) => toast.error(err, 'Could not delete webhook'),
  });

  if (locked) {
    return (
      <div className="card border-dashed border-2 border-gray-200 bg-gray-50/60">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Lock className="w-4 h-4" />
            </span>
            Outbound webhooks
            <span className="pill bg-gray-100 text-gray-500">Locked</span>
          </div>
        </div>
        <p className="text-sm text-ink-500">
          Push every audit, rewrite, score drop, and bot-detection event to your own
          Zapier / Make / n8n endpoint. Upgrade to AutoPilot to enable webhooks.
        </p>
      </div>
    );
  }

  function toggleEvent(key) {
    setDraft((d) => ({
      ...d,
      events: d.events.includes(key)
        ? d.events.filter((e) => e !== key)
        : [...d.events, key],
    }));
  }

  function copySecret(secret) {
    navigator.clipboard.writeText(secret);
    toast.success('Signing secret copied');
  }

  const rows = webhooksQuery.data?.webhooks || [];
  const availableEvents = webhooksQuery.data?.availableEvents || Object.keys(EVENT_LABELS);

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Webhook className="w-4 h-4" />
          </span>
          Outbound webhooks
          {webhooksQuery.data && (
            <span className="text-xs text-ink-400 font-normal ml-1">({rows.length})</span>
          )}
        </div>
        {!adding && (
          <button type="button" className="btn-primary text-xs" onClick={() => setAdding(true)}>
            <Plus className="w-3 h-3" /> Add webhook
          </button>
        )}
      </div>

      <p className="text-sm text-ink-500 mb-3">
        Each event payload is HMAC-signed in <code>X-RankOnAI-Signature: sha256=...</code>
        using the secret you'll see once on creation. Verify it server-side to reject spoofed
        deliveries.
      </p>

      {adding && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-ink-600">
              Label (optional)
              <input
                className="input mt-1"
                placeholder="e.g. Zapier prod"
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
            </label>
            <label className="text-xs text-ink-600">
              URL
              <input
                className="input mt-1"
                placeholder="https://hooks.zapier.com/hooks/catch/..."
                value={draft.url}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
              />
            </label>
          </div>
          <div>
            <div className="text-xs text-ink-600 mb-1">Events</div>
            <div className="flex flex-wrap gap-1.5">
              {availableEvents.map((ev) => (
                <button
                  type="button"
                  key={ev}
                  className={`pill cursor-pointer ${
                    draft.events.includes(ev)
                      ? 'bg-brand-50 text-brand-700'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                  onClick={() => toggleEvent(ev)}
                >
                  {EVENT_LABELS[ev] || ev}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setAdding(false);
                setDraft({ label: '', url: '', events: ['audit.completed'] });
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={
                !draft.url || draft.events.length === 0 || createMutation.isPending
              }
              onClick={() => createMutation.mutate(draft)}
            >
              <Plus className="w-3 h-3" /> Create
            </button>
          </div>
        </div>
      )}

      {webhooksQuery.isLoading ? (
        <p className="text-sm text-ink-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-400">No webhooks yet. Click "Add webhook" to start.</p>
      ) : (
        <ul className="divide-y divide-gray-100 text-sm">
          {rows.map((w) => (
            <li key={w._id} className="py-3 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-ink-800">{w.label || '(unlabeled)'}</span>
                  {w.isActive ? (
                    <span className="pill bg-emerald-50 text-emerald-700">Active</span>
                  ) : (
                    <span className="pill bg-gray-100 text-gray-500">Paused</span>
                  )}
                  {w.consecutiveFailures > 0 && (
                    <span className="pill bg-rose-50 text-rose-700" title={w.lastError || ''}>
                      {w.consecutiveFailures} failed
                    </span>
                  )}
                </div>
                <div className="text-xs text-ink-500 font-mono break-all mt-0.5">{w.url}</div>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {w.events.map((ev) => (
                    <span key={ev} className="pill bg-gray-100 text-gray-600 text-[10px]">
                      {EVENT_LABELS[ev] || ev}
                    </span>
                  ))}
                </div>
                {revealedSecrets[w._id] && (
                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs">
                    <div className="font-semibold text-amber-900 mb-1">
                      Save this signing secret now — it won't be shown again
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="font-mono break-all flex-1">
                        {revealedSecrets[w._id]}
                      </code>
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => copySecret(revealedSecrets[w._id])}
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                  </div>
                )}
                {!revealedSecrets[w._id] && w.secret && (
                  <div className="text-[11px] text-ink-400 mt-1 font-mono">
                    Secret: {w.secret}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() =>
                    toggleMutation.mutate({ id: w._id, isActive: !w.isActive })
                  }
                  title={w.isActive ? 'Pause' : 'Resume'}
                >
                  <Power className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs text-rose-600 hover:text-rose-700"
                  onClick={() => {
                    if (window.confirm(`Delete webhook "${w.label || w.url}"?`)) {
                      deleteMutation.mutate(w._id);
                    }
                  }}
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
